"use client";

import { c as _c } from "react/compiler-runtime";
import { useMemo, useState } from "react";
import { useLocale } from "next-intl";
import { InstantSearchNext } from "react-instantsearch-nextjs";
import { searchClient, createTypeScopedSearchClient } from "@/lib/algolia/client";
import { getAlgoliaIndexName } from "@/lib/algolia/config";
import { consentGatedInsights } from "@/lib/algolia/insights-client";
import { getDirectoryIndexName, LISTING_TYPE_TO_ALGOLIA_TYPE } from "./algolia-listing.config";
import { LISTING_TYPE_CONFIGS } from "../directory-listing.config";
import { AlgoliaDirectoryView } from "./algolia-directory-view.component";
import type { FacetIdNameMaps, FullFacetValues } from "./directory-facet-maps";
import type { DirectoryListingProps } from "../directory-listing.types";
const DEFAULT_PAGE_SIZE = 24;

/**
 * Algolia-backed replacement for {@link DirectoryListing}. Wraps the directory
 * in an InstantSearch instance, then renders the shared
 * {@link DirectoryListingView} driven by InstantSearch hooks (see
 * {@link AlgoliaDirectoryView}, which also owns the `Configure` widget so the
 * type/time/date filters can be computed from component state).
 *
 * `InstantSearchNext` performs the initial search during the server render, so
 * the listing is server-rendered for first paint / SEO and hydrates in place.
 */
export function AlgoliaDirectoryListing(t0) {
  const $ = _c(21);
  const {
    listingType,
    pageSize: t1,
    facetMaps,
    fullFacets,
    hasHeader: t2,
    categoryMenu,
    categoryMenuAllHref
  } = t0;
  const pageSize = t1 === undefined ? DEFAULT_PAGE_SIZE : t1;
  const hasHeader = t2 === undefined ? false : t2;
  const locale = useLocale();
  let t3;
  if ($[0] !== listingType || $[1] !== locale) {
    t3 = getDirectoryIndexName(getAlgoliaIndexName(locale), listingType);
    $[0] = listingType;
    $[1] = locale;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const indexName = t3;
  const [nowUnix] = useState(_temp);
  const hasTimeFilter = LISTING_TYPE_CONFIGS[listingType]?.hasTimeFilter;
  const defaultFilters = hasTimeFilter ? `endDate >= ${nowUnix}` : undefined;
  const t4 = LISTING_TYPE_TO_ALGOLIA_TYPE[listingType];
  let t5;
  if ($[3] !== defaultFilters || $[4] !== pageSize || $[5] !== t4) {
    t5 = createTypeScopedSearchClient(searchClient, t4, pageSize, defaultFilters);
    $[3] = defaultFilters;
    $[4] = pageSize;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const client = t5;
  let t6;
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      insightsClient: consentGatedInsights
    };
    t7 = {
      preserveSharedStateOnUnmount: true
    };
    $[7] = t6;
    $[8] = t7;
  } else {
    t6 = $[7];
    t7 = $[8];
  }
  let t8;
  if ($[9] !== categoryMenu || $[10] !== categoryMenuAllHref || $[11] !== facetMaps || $[12] !== fullFacets || $[13] !== hasHeader || $[14] !== listingType || $[15] !== pageSize) {
    t8 = <AlgoliaDirectoryView listingType={listingType} pageSize={pageSize} facetMaps={facetMaps} fullFacets={fullFacets} hasHeader={hasHeader} categoryMenu={categoryMenu} categoryMenuAllHref={categoryMenuAllHref} />;
    $[9] = categoryMenu;
    $[10] = categoryMenuAllHref;
    $[11] = facetMaps;
    $[12] = fullFacets;
    $[13] = hasHeader;
    $[14] = listingType;
    $[15] = pageSize;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== client || $[18] !== indexName || $[19] !== t8) {
    t9 = <InstantSearchNext indexName={indexName} searchClient={client} insights={t6} future={t7}>{t8}</InstantSearchNext>;
    $[17] = client;
    $[18] = indexName;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  return t9;
}
function _temp() {
  return Math.floor(Date.now() / 1000);
}